import { addMonths, format } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { type ChevronProps, type MonthCaptionProps, useDayPicker } from 'react-day-picker';
import { popoverScrollFix } from '../lib/popover-scroll-fix';
import { cn } from '../lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '../primitives/popover';

/**
 * The one Numera calendar skin, shared by every calendar in the app.
 *
 * It lives here rather than in `date-picker.tsx` because there are two
 * calendars — the single-date picker and the two-month range panel — and they
 * were visibly different objects: one had the brand's round day cells and
 * spelled-out weekdays, the other still had shadcn's defaults. A skin defined
 * at one of the two call sites is a skin the other one drifts from, which is
 * exactly what happened.
 *
 * What differs between the two is the *arrangement* (`months`: one column or
 * two side by side) and what "selected" means (one day, or a span). Everything
 * else — the day grid, the caption, the chevrons, the weekday names — is
 * identical and comes from here.
 */

const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** Oldest year the jump list offers when the calendar has no `startMonth`. */
const DEFAULT_FIRST_YEAR = 1980;
/** How far past the current year it runs when there is no `endMonth` — contract
 *  and renewal dates are routinely a few years out, so the list has to reach
 *  them. */
const DEFAULT_YEARS_AHEAD = 10;

/**
 * The caption's month and year, each a button opening a jump panel — twelve
 * months in a grid, years in a scrolling list. Getting to a date two years back
 * is one click and one click rather than twenty-four presses of a chevron.
 *
 * Bounds come from the calendar's own `startMonth`/`endMonth` when it has them,
 * so a calendar that is limited to a window offers only that window here.
 * `displayIndex` is what makes this correct on the two-month panel: picking
 * March in the RIGHT-hand caption has to leave the calendar showing Feb–Mar,
 * not Mar–Apr, so the target month is offset back by the caption's position —
 * the same arithmetic react-day-picker's own dropdowns do.
 *
 * The panels are Popovers, so they portal to `document.body`: the calendar's
 * own panel clips its content, and an in-flow dropdown would be cut off by it.
 * Being descendants of that popover in the React tree is what keeps a click
 * inside them from dismissing the calendar itself.
 */
export function CaptionJump({ calendarMonth, displayIndex, ...divProps }: MonthCaptionProps) {
  const { goToMonth, dayPickerProps } = useDayPicker();
  const [monthOpen, setMonthOpen] = useState(false);
  const [yearOpen, setYearOpen] = useState(false);
  const captionRef = useRef<HTMLDivElement>(null);
  // The calendar's own popover panel. Used as the collision boundary for the
  // two jump panels so they are measured and clamped against the picker rather
  // than against the viewport — without it a twelve-year list is simply as tall
  // as it wants to be and hangs out of the bottom of the calendar.
  const [boundary, setBoundary] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setBoundary(captionRef.current?.closest<HTMLElement>('[data-slot="popover-content"]') ?? null);
  }, []);

  const { startMonth, endMonth } = dayPickerProps;
  const shown = calendarMonth.date;
  const shownMonth = shown.getMonth();
  const shownYear = shown.getFullYear();

  const firstYear = startMonth ? startMonth.getFullYear() : DEFAULT_FIRST_YEAR;
  const lastYear = endMonth
    ? endMonth.getFullYear()
    : new Date().getFullYear() + DEFAULT_YEARS_AHEAD;

  // This caption may not be the first month on screen, so the month it names
  // is `displayIndex` months after the one the calendar is anchored to.
  const jumpTo = (year: number, month: number) =>
    goToMonth(addMonths(new Date(year, month, 1), -displayIndex));

  const years: number[] = [];
  for (let year = lastYear; year >= firstYear; year--) years.push(year);

  const monthOutOfRange = (index: number) => {
    const first = new Date(shownYear, index, 1);
    const last = new Date(shownYear, index + 1, 0);
    if (startMonth && last < startMonth) return true;
    if (endMonth && first > endMonth) return true;
    return false;
  };

  return (
    <div ref={captionRef} {...divProps}>
      <Popover open={monthOpen} onOpenChange={setMonthOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Choose month"
            className="rounded-pill px-1.5 py-0.5 text-data font-medium transition-colors hover:text-accent-foreground"
          >
            {format(shown, 'MMMM')}
          </button>
        </PopoverTrigger>
        <PopoverContent
          data-picker="date"
          className="w-48 p-2"
          align="center"
          collisionBoundary={boundary ?? undefined}
          collisionPadding={8}
        >
          <div className="grid grid-cols-3 gap-1">
            {MONTHS_SHORT.map((label, index) => (
              <button
                key={label}
                type="button"
                disabled={monthOutOfRange(index)}
                onClick={() => {
                  jumpTo(shownYear, index);
                  setMonthOpen(false);
                }}
                className={cn(
                  'rounded-pill px-2 py-1.5 text-data transition-colors disabled:pointer-events-none disabled:opacity-40',
                  index === shownMonth
                    ? 'bg-primary font-medium text-primary-foreground'
                    : 'font-normal hover:bg-accent light:hover:bg-(--menu-hover-bg)',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      <Popover open={yearOpen} onOpenChange={setYearOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Choose year"
            className="num rounded-pill px-1.5 py-0.5 text-data font-medium transition-colors hover:text-accent-foreground"
          >
            {shownYear}
          </button>
        </PopoverTrigger>
        {/* popoverScrollFix: this list is portaled, so when the calendar sits in
            a Sheet or Dialog the modal's scroll lock swallows the wheel and the
            list only moves by keyboard. */}
        <PopoverContent
          data-picker="date"
          className="w-32 space-y-0.5 overflow-y-auto p-2 max-h-[var(--radix-popover-content-available-height,16rem)]"
          align="center"
          collisionBoundary={boundary ?? undefined}
          collisionPadding={8}
          {...popoverScrollFix}
        >
          {years.map((year) => {
            const isShown = year === shownYear;
            return (
              <button
                key={year}
                type="button"
                // Opening a decade away from the year in the caption would look
                // like the list is broken, so the current one is centred.
                ref={isShown ? (el) => el?.scrollIntoView({ block: 'center' }) : undefined}
                onClick={() => {
                  jumpTo(year, shownMonth);
                  setYearOpen(false);
                }}
                className={cn(
                  'num w-full rounded-pill px-3 py-1.5 text-center text-data transition-colors',
                  isShown
                    ? 'bg-primary font-medium text-primary-foreground'
                    : 'font-normal hover:bg-accent light:hover:bg-(--menu-hover-bg)',
                )}
              >
                {year}
              </button>
            );
          })}
        </PopoverContent>
      </Popover>
    </div>
  );
}

/**
 * `Chevron` is restated alongside `MonthCaption`, not left to the mirror. The
 * mirrored `Calendar` spreads its own props AFTER its `components`, so passing
 * a `components` object replaces the mirror's whole one — leaving this out
 * silently swaps lucide's chevrons for react-day-picker's built-in SVG.
 */
export const CALENDAR_COMPONENTS = {
  MonthCaption: CaptionJump,
  Chevron: ({ orientation }: ChevronProps) =>
    orientation === 'left' ? (
      <ChevronLeft className="size-4" />
    ) : (
      <ChevronRight className="size-4" />
    ),
};

/** "Sun", not react-day-picker's default "Su" — the reference design spells the
 *  day out, and at 36px columns there is room for it. */
export const CALENDAR_FORMATTERS = { formatWeekdayName: (day: Date) => format(day, 'ccc') };

/**
 * Everything inside one month: the caption row, the chevrons and the day grid.
 *
 * The three header slots are placed by explicit `col-start` / `row-start`
 * rather than by source order, and that is load-bearing. With
 * `navLayout="around"` react-day-picker renders the previous-month button only
 * on the FIRST month and the next-month button only on the LAST, so on the
 * two-month panel the right-hand month's children are `[caption, next, grid]`
 * — flow order would put its caption where the left one's chevron goes. Naming
 * the cells makes both months line up under the same rule.
 *
 * It also retires the old absolute-positioning hack. The caption used to be
 * laid over the chevrons and swallowed the clicks on them, which needed a
 * `z-10` counter-hack to undo; on the range panel it was worse, and dropped the
 * previous-month chevron on top of the preset list. Nothing overlaps now.
 *
 * react-day-picker puts the modifier classes (`selected`, `today`, `disabled`,
 * `outside`, and the `range_*` trio) on the `<td>` GRIDCELL and only
 * `day_button` on the `<button>` inside it, so every day-level colour below is
 * written as a child selector off the cell.
 *
 * The COLUMN WIDTH is on the cell for the same reason, and it is load-bearing.
 * A week is a flex row of seven `<td>`s, but the days before the 1st render as
 * cells with NO BUTTON IN THEM — so with the width only on `day_button` those
 * cells collapsed to nothing and the first row packed left, putting a Tuesday
 * 1st under Sunday. `size-9` on the cell is what holds an empty column open.
 *
 * The hover wash is on the cell too, guarded with
 * `:not([data-selected]):not([data-disabled])` so it cannot match a day that is
 * already chosen. "Hovered" and "selected" therefore never race on specificity
 * — the selected day stays blue while the cursor is on it, instead of
 * flickering to the hover tint.
 *
 * Hover is the lime `--menu-hover-bg` the menus and table rows use, not the
 * blue `--accent`: blue means *selected* everywhere in this app, so a hovered
 * day washing blue reads as already picked.
 */
const CALENDAR_MONTH_CLASS_NAMES = {
  month: 'grid grid-cols-[auto_1fr_auto] items-center gap-x-2 gap-y-4',
  month_caption: 'col-start-2 row-start-1 flex items-center justify-center gap-0.5',
  caption_label: 'text-data font-medium',
  button_previous:
    'col-start-1 row-start-1 inline-flex size-7 items-center justify-center rounded-pill border border-(--control-border) text-muted-foreground transition-colors light:bg-card light:hover:bg-(--menu-hover-bg) light:hover:text-foreground aria-disabled:pointer-events-none aria-disabled:opacity-40',
  button_next:
    'col-start-3 row-start-1 inline-flex size-7 items-center justify-center rounded-pill border border-(--control-border) text-muted-foreground transition-colors light:bg-card light:hover:bg-(--menu-hover-bg) light:hover:text-foreground aria-disabled:pointer-events-none aria-disabled:opacity-40',
  month_grid: 'col-span-3 row-start-2 w-full border-collapse',
  weekdays: 'flex',
  weekday: 'w-9 text-meta font-medium text-ink-faint',
  week: 'mt-1 flex w-full',
  day: 'relative size-9 p-0 text-center focus-within:relative focus-within:z-20 [&:not([data-selected]):not([data-disabled])>button]:hover:bg-accent light:[&:not([data-selected]):not([data-disabled])>button]:hover:bg-(--menu-hover-bg) light:[&:not([data-selected]):not([data-disabled])>button]:hover:text-foreground',
  day_button:
    'num inline-flex size-9 items-center justify-center rounded-pill text-data font-normal transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none',
  // Today, when it is not the selected day: a blue hairline ring and a weight
  // step, never a fill. A fill would be a second "chosen" day on the grid.
  today:
    '[&:not([data-selected])>button]:font-semibold [&:not([data-selected])>button]:text-accent-foreground light:[&:not([data-selected])>button]:ring-1 light:[&:not([data-selected])>button]:ring-inset light:[&:not([data-selected])>button]:ring-primary/50',
  disabled: '[&>button]:text-ink-faint [&>button]:opacity-50',
  // Stated rather than left to the mirror's default, which is `day-outside
  // text-muted-foreground …` and was leaking onto every blank cell.
  outside: '[&>button]:text-ink-faint',
  hidden: 'invisible',
};

/** One month, one chosen day: the day is a solid blue disc. */
export const CALENDAR_SINGLE_CLASS_NAMES = {
  ...CALENDAR_MONTH_CLASS_NAMES,
  months: 'flex flex-col',
  selected:
    '[&>button]:bg-primary [&>button]:font-medium [&>button]:text-primary-foreground [&>button]:shadow-xs',
};

/**
 * Two months, a chosen span.
 *
 * The band is painted on the CELL and the two ends on the BUTTON, which is what
 * makes both readable at once: a continuous pale-blue run showing the extent,
 * with the two dates the user actually named as solid discs on top of it. The
 * ends round the band's outer corner, so a one-day range — where `range_start`
 * and `range_end` land on the same cell — comes out as a single pill rather
 * than a square block.
 *
 * `selected` is deliberately empty. react-day-picker puts it on every day in
 * the span as well as the `range_*` classes, so the single-day disc above would
 * paint the whole run blue.
 */
export const CALENDAR_RANGE_CLASS_NAMES = {
  ...CALENDAR_MONTH_CLASS_NAMES,
  months: 'flex flex-col gap-4 sm:flex-row sm:gap-6',
  selected: '',
  range_middle: 'bg-accent [&>button]:rounded-none [&>button]:text-accent-foreground',
  range_start:
    'bg-accent rounded-l-pill [&>button]:bg-primary [&>button]:font-medium [&>button]:text-primary-foreground',
  range_end:
    'bg-accent rounded-r-pill [&>button]:bg-primary [&>button]:font-medium [&>button]:text-primary-foreground',
};
